import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import type {
  OrderFormFieldDetail,
  OrderFormFieldListParams,
  OrderFormFieldPayload,
  OrderFormFieldSummary,
  OrderFormMall,
} from '../types/orderFormField';

/**
 * 글로벌 주문서 추가정보(통관 필드) admin CRUD. (be-order 계약 — order-service admin.)
 * 게이트웨이 `/admin/v1/orders/**` → BE `/api/admin/v1/orders/**` RewritePath 재사용 가정
 * (orderPolicy/shippingFee 와 동일). v2 envelope `{ success, data, error, meta }`.
 * write 는 Idempotency-Key 수용(lenient). 경로가 generated OpenAPI 스펙에 미반영이라
 * orderPolicy/shippingFee 와 동일하게 adminFetch 로 직접 호출한다.
 *
 * be-order 계약 확정: 목록 응답 = `data.fields[]`(전체 필드), 단건/생성/수정 =
 * OrderFormFieldAdminResponse, DELETE=soft-delete(data null, 과거 주문 값 보존).
 * 목록 필터 `?mall=zh-CN`(옵션). RBAC=SUPER_ADMIN/OPS_ADMIN.
 */
const BASE = '/admin/v1/orders/order-form-fields';

interface ListEnvelope {
  success: boolean;
  data?: { fields?: OrderFormFieldSummary[] | null } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: OrderFormFieldDetail | null;
}

/**
 * 상세를 정규화한다 — BE 가 options 를 null 로 줄 수도 있어 렌더 전에 배열 보장
 * (`.map` 크래시 방지). shippingFee 정책 hooks 의 동일 가드.
 */
const normalizeDetail = (p: OrderFormFieldDetail): OrderFormFieldDetail => ({
  ...p,
  options: (p.options ?? []).filter((o) => typeof o === 'string'),
});

interface ErrorEnvelopeShape {
  error?: { code?: string; message?: string } | null;
}

/**
 * write(POST/PUT) 실패 시 BE 에러 envelope(`{error:{code,message}}`)를 파싱해
 * `ApiClientError` 로 던진다. friendlyMessage 가 BE 메시지를 그대로 노출하므로
 * RRN 하드거부(ORDER_ADDITIONAL_INPUT_INVALID, "주민등록번호는 법령상 수집할 수
 * 없습니다…") 같은 검증 메시지가 토스트에 정확히 표시된다(be-order #3).
 * 본문 파싱 실패(비-envelope)면 status 기반 일반 Error 로 폴백.
 */
const throwWriteError = async (res: Response, what: string): Promise<never> => {
  try {
    const body = (await res.json()) as ErrorEnvelopeShape;
    const err = body?.error;
    if (err?.code || err?.message) {
      throw new ApiClientError(
        { code: err.code ?? 'COMMON_BAD_REQUEST', message: err.message ?? '' },
        { status: res.status },
      );
    }
  } catch (e) {
    if (e instanceof ApiClientError) throw e;
    // JSON 파싱 실패 등은 무시하고 일반 Error 폴백.
  }
  throw new Error(`order form field ${what} failed: ${res.status}`);
};

export const orderFormFieldsQueryKey = (mall: OrderFormMall) =>
  ['admin', 'order-form-fields', mall] as const;

export const orderFormFieldQueryKey = (fieldId: string) =>
  ['admin', 'order-form-field', fieldId] as const;

const fetchList = async (
  params: OrderFormFieldListParams,
): Promise<OrderFormFieldSummary[]> => {
  const qs = new URLSearchParams({ mall: params.mall }).toString();
  const res = await adminFetch(`${BASE}?${qs}`);
  if (!res.ok) throw new Error(`order form fields list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  // 목록도 전체 필드(options 포함)라 동일 정규화로 null options 가드.
  return (env.data?.fields ?? []).map(normalizeDetail);
};

const fetchOne = async (fieldId: string): Promise<OrderFormFieldDetail> => {
  const res = await adminFetch(`${BASE}/${fieldId}`);
  if (!res.ok) throw new Error(`order form field fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return normalizeDetail(env.data);
};

export const useOrderFormFieldsQuery = (mall: OrderFormMall) =>
  useQuery<OrderFormFieldSummary[]>({
    queryKey: orderFormFieldsQueryKey(mall),
    queryFn: () => fetchList({ mall }),
  });

export const useOrderFormFieldQuery = (fieldId: string | null) =>
  useQuery<OrderFormFieldDetail>({
    queryKey: orderFormFieldQueryKey(fieldId ?? ''),
    queryFn: () => fetchOne(fieldId as string),
    enabled: fieldId != null,
  });

export const useOrderFormFieldMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'order-form-fields'] });

  const create = useMutation({
    mutationFn: async (
      payload: OrderFormFieldPayload,
    ): Promise<OrderFormFieldDetail> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload),
        },
        body: JSON.stringify(payload),
      });
      // 400 RRN 거부 등 BE 검증 메시지를 그대로 노출(be-order #3).
      if (!res.ok) await throwWriteError(res, 'create');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      fieldId,
      payload,
    }: {
      fieldId: string;
      payload: OrderFormFieldPayload;
    }): Promise<OrderFormFieldDetail> => {
      const path = `${BASE}/${fieldId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', path, payload),
        },
        body: JSON.stringify(payload),
      });
      // 400 RRN 거부 등 BE 검증 메시지를 그대로 노출(be-order #3).
      if (!res.ok) await throwWriteError(res, 'update');
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return normalizeDetail(env.data);
    },
    onSuccess: (_, { fieldId }) => {
      void qc.invalidateQueries({ queryKey: orderFormFieldQueryKey(fieldId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (fieldId: string): Promise<void> => {
      const path = `${BASE}/${fieldId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('DELETE', path, ''),
        },
      });
      if (!res.ok) throw new Error(`order form field delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};
